/**
 * What the portal is entitled to say after POST /v2/kafka/send_message returns envelope 200.
 *
 * BEFORE BE-074 IT KNEW ALMOST NOTHING. The response carried Payload, PayloadPriority, PayloadType,
 * PayloadId, PayloadExpiryDateTime and QueuedMessages — no delivery status, no online flag, no
 * per-device breakdown. So the toast said "Command successfully sent to device." on an envelope 200
 * and was false whenever the vehicle was not there.
 *
 * SINCE BE-074 THE RESPONSE REPORTS THE OUTCOME, PER DEVICE. Captured off the wire from a local
 * backend, not copied from a design document:
 *
 *   {"Status":200,"Message":"Success","Response":{"Recipients":[
 *     {"DeviceId":2,"PayloadId":34463,"Disposition":"DISPATCHED"}]}}
 *
 * KEY THE COPY ON `Disposition`, NEVER ON THE ExpiryMode WE SENT. That is the whole ticket, and the
 * captures prove the inference is unsound rather than merely risky — cross-checked against the rows
 * by the backend session:
 *
 *   34463  IMMEDIATE_EXPIRE + online   ->  DISPATCHED   row STORED
 *   34465  IMMEDIATE_EXPIRE + offline  ->  DISCARDED    row EXPIRED at creation
 *   34464  NEVER_EXPIRE     + offline  ->  QUEUED       row PENDING, visible to the drain
 *
 * The first two carry the SAME requested expiry and came back opposite. And BE-073 severed the link
 * further: a reset asked for with IMMEDIATE_EXPIRE is now held rather than discarded, so the request
 * stopped predicting the result even for a single mode.
 *
 * NOTE none of those three captures is a reset (`reset=false` on all rows). They exercise the
 * ordinary path, not BE-073's guarded one.
 */

/** One device's outcome, as BE-074 serialises it. */
export interface RecipientOutcome {
  DeviceId: number
  PayloadId: number
  Disposition: string
}

/**
 * The call sites this rule governs, relative to the repo root.
 *
 * FE-031 was filed against Admin Commands because that is where it was noticed, and recorded
 * "whether any other portal surface makes the same claim" as NOT ESTABLISHED. It is established:
 * all five claimed delivery, and the three driver-message ones hardcode NEVER_EXPIRE, so they queue
 * by design for an absent vehicle and were false on routine daily traffic.
 *
 * ADD A NEW KafkaSendMessage CALL SITE TO THIS LIST. The test that reads these files is what makes
 * the rule real; a helper nobody calls is the defect this codebase keeps re-finding.
 */
export const COMMAND_SEND_SITES = [
  'src/app/modules/commands/components/AdminCommands.tsx',
  'src/app/modules/device/components/ResetDeviceConfirmation.tsx',
  'src/app/modules/message/DriverMessage.tsx',
  'src/app/modules/device/components/MessageDevice.tsx',
  'src/app/modules/device/components/Device/SendDriverMessageTab.tsx',
]

/**
 * Pulls the per-device outcomes out of a response body, tolerating every shape that is not one.
 *
 * Returns [] rather than throwing for an older backend, a changed shape, or the resolved-error
 * object the shared axios interceptor hands back (api.tsx:834 returns `error` instead of
 * rejecting, so a failed request arrives looking like a response). The caller then says only that
 * the server accepted it, which remains true of an envelope 200 and is all we had before BE-074.
 */
export const recipientsOf = (body: any): RecipientOutcome[] => {
  const recipients = body?.Response?.Recipients
  return Array.isArray(recipients) ? recipients : []
}

const ARRIVAL_VERB = /\b(sent|delivered|received|shown|displayed|reached)\b/i

/**
 * NEGATED ARRIVAL IS THE OPPOSITE OF A CLAIM, and an earlier version of this function got that
 * wrong — it matched the word rather than the assertion, so "it will NOT reach the vehicle" was
 * flagged as a delivery claim. Its own test caught it, which is the argument for the test existing.
 *
 * Bounded at sentence punctuation so negation cannot become a loophole: "It was not queued. It was
 * sent to the device." is still caught.
 */
const stripNegatedArrival = (copy: string): string =>
  copy.replace(/\b(not|never|nothing|won't|will not|cannot|can't)\b[^.;—-]*/gi, '')

/** Whether a sentence asserts that something reached a vehicle. */
export const claimsDelivery = (copy: string): boolean =>
  ARRIVAL_VERB.test(stripNegatedArrival(copy))

const vehicles = (n: number): string => (n === 1 ? '1 vehicle' : `${n} vehicles`)

/**
 * Per-disposition wording. Deliberately avoids the verbs in `ARRIVAL_VERB` rather than relying on
 * `claimsDelivery` to police them — a constraint that forces honest phrasing instead of a
 * carefully-negated version of the dishonest one. (DISCARDED does say "will not reach": `reach` is
 * not in `ARRIVAL_VERB`, which lists `reached`, so it is safe on the word list AND on negation
 * stripping. It is a denial of arrival, not an assertion of it.)
 *
 * DISPATCHED IS NOT DELIVERED, AND IT IS NOT EVEN "THE CONNECTION WAS LIVE". An earlier version of
 * this line said "on a live connection" and the backend session refuted it with two measurements:
 * the branch reads `device.isDeviceOnline`, a FLAG maintained by a sweeper on a 15 s fixedDelay —
 * tonight the radio dropped at 21:56:49 and the flag did not turn until 21:57:03, fourteen seconds
 * in which a send would have reported DISPATCHED with nothing at the other end — and
 * `SocketServer.send` is `convertAndSendToUser`, fire and forget, with no return value, no
 * exception and no signal if nobody is subscribed. So the honest claim is that the server WROTE the
 * frame to a vehicle it BELIEVED was online.
 *
 * It is also not an outcome at the vehicle: payload 34462 went SENT at 22:01:38 and SUCCESS at
 * 22:01:46, and payload 34460 was dispatched to an online vehicle whose app REFUSED it 128 ms
 * later. The sentence has to say what has not happened as well as what has.
 *
 * QUEUED MUST CARRY ITS ESCAPE CLAUSE. "Held until that vehicle reconnects" was FALSE as written,
 * and false in the common case: the reconnect drain keeps ONE message per payload type per device
 * and marks the rest REDUNDANT — 16755 REDUNDANT rows against 6368 PENDING in the clone, so being
 * superseded is roughly two and a half times more common than still waiting. OBSOLETE is a second
 * path (BE-3, TemporaryMessageService).
 *
 * The timing is what makes the unqualified promise indefensible: supersession fires AT RECONNECT.
 * The vehicle comes back exactly as the sentence said it would, and the command still never goes
 * out. An operator who watched that happen would be entitled to call the copy a lie — which is the
 * defect this whole ticket exists to remove, reappearing inside the fix for it.
 */
const PHRASING: Record<string, (n: number) => string> = {
  DISPATCHED: (n) =>
    n === 1
      ? `Written out to 1 vehicle the server last saw online — no confirmation from it yet.`
      : `Written out to ${n} vehicles the server last saw online — no confirmation from them yet.`,
  QUEUED: (n) =>
    n === 1
      ? `1 vehicle is offline. It is kept and goes out when that vehicle reconnects, unless a newer command of the same kind supersedes it first.`
      : `${n} vehicles are offline. It is kept and goes out when they reconnect, unless a newer command of the same kind supersedes it first.`,
  DISCARDED: (n) =>
    n === 1
      ? `1 vehicle is offline, so it was discarded and will not reach that vehicle.`
      : `${n} vehicles are offline, so it was discarded and will not reach them.`,
}

/**
 * THE ONE THING THE PORTAL STILL CANNOT SEE. None of these dispositions covers a command that
 * reached the app and was REFUSED — that outcome arrives asynchronously (34460, 128 ms after the
 * send) and the send response cannot carry it. `DISPATCHED` is therefore consistent with a refusal
 * that has not happened yet, which is why its wording withholds rather than reassures.
 *
 * Reporting a refusal needs a status surface the portal does not have. Searched `api.tsx`: nothing
 * there reports per-device message delivery or refusal. `getListmessages` is NOT that endpoint — it
 * fetches a customer's message LIBRARY (`/customers/{id}/messages/`) and has two live callers in the
 * playlist module. `GET_STATUS_LOGS` is positional telemetry, not message state. Separate work.
 */
export const acceptanceMessage = (
  subject: 'Command' | 'Message',
  recipients: RecipientOutcome[]
): string => {
  const opener = `${subject} accepted by the server.`

  if (!recipients.length) {
    return `${opener} It did not report what happened to the vehicle.`
  }

  const counts = new Map<string, number>()
  recipients.forEach((r) => {
    const key = r?.Disposition || ''
    counts.set(key, (counts.get(key) || 0) + 1)
  })

  const parts: string[] = []
  const unrecognised: string[] = []
  counts.forEach((n, disposition) => {
    const phrase = PHRASING[disposition]
    if (phrase) {
      parts.push(phrase(n))
    } else {
      // Named explicitly rather than folded into a soothing generic. A mapping that falls through
      // by mistake must look like a gap, not like a deliberate sentence — the backend session's
      // caution, and it is right: a reassuring fallback for a KNOWN value is worse than a blank.
      unrecognised.push(`${vehicles(n)} (${disposition || 'no disposition'})`)
    }
  })

  if (unrecognised.length) {
    parts.push(`The portal cannot describe the outcome for ${unrecognised.join(', ')}.`)
  }

  return `${opener} ${parts.join(' ')}`
}
